// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — the three modes. A mode has to change how the game PLAYS, not just a number, so all four
// dials move together and each one changes a different thing:
//
//   • the RADIUS changes how much room a ring has to run (31 / 55 / 85 points),
//   • the RUN LENGTH changes how a line is built — four is a thing you can improvise, five is a
//     thing you have to set up, and on the 31-point board the longest line is only six, so five
//     there would be a game nobody could ever score in,
//
// EVERY ONE OF THESE NUMBERS WAS MEASURED, NOT CHOSEN (tests/balance.test.ts, and the sweep whose
// results are quoted in the build log). The middle mode was originally 55 points with a run of FIVE,
// which looked obviously right and was not: the field clogged before three claims landed, so 18% of
// games between two strong engines ended with nobody able to ride and 13% were outright DRAWS.
// Dropping that mode's run to four does not make it easier — it makes claims frequent enough to keep
// clearing studs off the field — and it takes stalls and draws to ZERO with a 7% blowout rate. Two
// other "obvious" fixes were tried first and both failed: fewer rings barely moved it (13% stalls),
// and a lower target cured the stall by replacing it with a 39% blowout rate.

//   • the RING COUNT changes what a claim costs you (a third of your force, or a fifth),
//   • the TARGET changes how long the whole thing lasts.
//
// The board's bounding box is near-square at every radius (√3·R by 2R−1), so all three share ONE
// board box and differ only in density: 68px, 51px and 41px between neighbouring points at a 375px
// phone width. That is the reason the biggest mode is playable with a thumb at all.
//
// The HOST's pick travels frozen inside rematch.ts's round start and the board is derived from the
// mode alone, so a mode can never desync. modeOf() guards an id off the wire with Object.hasOwn — a
// bare `MODES[id] ||` lets 'constructor' through as a Mode of undefined fields.

import type { Shape } from './rules';

export interface Mode extends Shape {
  name: string;
  blurb: string;
  /** One line for the how-to-play card, which has to state the run length per mode. */
  rule: string;
}

export const MODES: Record<string, Mode> = {
  coil: {
    id: 'coil',
    name: 'Coil',
    R: 3,
    runLen: 4,
    rings: 3,
    target: 2,
    blurb: '31 points, three torcs each. Four in a line, two to win — and giving up one torc in three really hurts. Tight enough that the field sometimes jams with nobody able to ride, which is a draw.',
    rule: 'four in a line',
  },
  twist: {
    id: 'twist',
    name: 'Twist',
    R: 4,
    runLen: 4,
    rings: 5,
    target: 3,
    blurb: '55 points, five torcs each. Four in a line, three claims to win — the full shape of the game.',
    rule: 'four in a line',
  },
  hoard: {
    id: 'hoard',
    name: 'Hoard',
    R: 5,
    runLen: 5,
    rings: 5,
    target: 3,
    blurb: '85 points, five torcs each, and a line of FIVE. Room to manoeuvre, longer lines to build, and about twice the game.',
    rule: 'five in a line',
  },
};

export const MODE_IDS: string[] = ['coil', 'twist', 'hoard'];
/**
 * Twist is the default because it is the best-behaved shape the sweep found: no jams, no draws, a 7%
 * blowout rate and about 34 rides. Coil is smaller and quicker but jams to a draw roughly one game
 * in twelve, which is a poor first impression; its blurb says so rather than surprising anyone.
 */
export const DEFAULT_MODE = 'twist';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}
